import { supabase } from "@/integrations/supabase/client";
import { fetchCurrentCompanyId } from "@/hooks/useCurrentCompany";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type AuditedApplianceField = "category" | "brand" | "model_number" | "serial_number";

export interface ApplianceFieldChange {
  field: AuditedApplianceField;
  oldValue: string | null | undefined;
  newValue: string;
}

/** One audit row per field whose trimmed value actually changed. */
export function auditRowsFor(
  base: { appliance_id: string; company_id: string; changed_by: string; changed_by_email: string | null },
  changes: ApplianceFieldChange[],
) {
  return changes
    .map((c) => ({ ...c, prev: (c.oldValue ?? "").trim(), next: c.newValue.trim() }))
    .filter((c) => c.prev !== c.next)
    .map((c) => ({
      ...base,
      field: c.field,
      old_value: c.prev || null,
      new_value: c.next || null,
      action: c.prev ? "changed" : "added",
    }));
}

/**
 * Best-effort audit log of appliance identity changes (FIX-286).
 * Never throws — failure to log must not block the user action.
 */
export async function logApplianceFieldChanges(applianceId: string, changes: ApplianceFieldChange[]): Promise<void> {
  try {
    if (!applianceId || !UUID_RE.test(applianceId)) return;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const companyId = await fetchCurrentCompanyId();
    if (!companyId) return;
    const rows = auditRowsFor(
      { appliance_id: applianceId, company_id: companyId, changed_by: user.id, changed_by_email: user.email ?? null },
      changes,
    );
    if (!rows.length) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const table = supabase.from("appliance_serial_audit") as any;
    const { error } = await table.insert(rows);
    if (!error) return;
    // Until the `field` column exists, keep logging serial changes the old way.
    if (/field/i.test(error.message ?? "") || error.code === "PGRST204" || error.code === "42703") {
      const serialRows = rows.filter((r) => r.field === "serial_number").map(({ field: _f, ...r }) => r);
      if (serialRows.length) {
        const retry = await (supabase.from("appliance_serial_audit") as any).insert(serialRows);
        if (retry.error) console.warn("[appliance-audit] insert failed", retry.error);
      }
      console.warn("[appliance-audit] field column missing; non-serial changes not logged");
      return;
    }
    console.warn("[appliance-audit] insert failed", error);
  } catch (err) {
    console.warn("[appliance-audit] unexpected error", err);
  }
}

/** Serial-only wrapper kept for existing callers. */
export async function logSerialChange(params: {
  applianceId: string;
  oldValue: string | null | undefined;
  newValue: string;
}): Promise<void> {
  await logApplianceFieldChanges(params.applianceId, [
    { field: "serial_number", oldValue: params.oldValue, newValue: params.newValue },
  ]);
}
